import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, Input, Output } from '@angular/core';
import { type Observable } from 'rxjs';
import { SettingsFacade } from '@web/store/website/settings/settings.facade';
import type { CatalogProductCard } from '@web/widgets/product-card/type/product-card-widget.type';
import { RecurringMode } from '@web/widgets/product-card/type/product-card-widget.type';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe, DecimalPipe } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { injectPaymentMethodsQuery } from '@integrations/payment-methods';
import { EPaymentType } from '@common/enums';
import { FmFileUrlPipe } from '@sendpulse/file-manager';

@Component({
    selector: 'web-catalog-card',
    templateUrl: './catalog-card.component.html',
    styleUrls: ['./catalog-card.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [TooltipModule, AsyncPipe, TranslateModule, DecimalPipe, FmFileUrlPipe],
})
export class CatalogCardComponent {
    @Input() element: CatalogProductCard;
    @Output() setProductCardEvenEmitter: EventEmitter<number> = new EventEmitter<number>();

    private readonly settingsFacade: SettingsFacade = inject(SettingsFacade);
    private readonly paymentMethodsQuery = injectPaymentMethodsQuery();

    public readonly shopCurrency$: Observable<string> = this.settingsFacade.shopCurrency$;
    public readonly hasWayForPay = computed(() =>
        (this.paymentMethodsQuery.data() ?? []).some((paymentMethod) => paymentMethod.type === EPaymentType.wayforpay)
    );

    public get price(): number {
        const base = this.basePrice;
        if (!base) return 0;

        const applyTax = this.shouldApplyTax;
        const taxPercent = applyTax ? Number(this.element.taxes[0].value ?? 0) : 0;

        const result = applyTax ? base * (1 + taxPercent / 100) : base;
        return this.roundToCents(result);
    }

    private get shouldApplyTax(): boolean {
        return (
            Array.isArray(this.element?.taxes) &&
            this.element.taxes.length > 0 &&
            !this.element.taxes[0].isAddedToAmount
        );
    }

    private get basePrice(): number {
        return Number(this.element?.prices?.[0]?.value ?? 0);
    }

    private roundToCents(value: number): number {
        const factor = 100;
        return Math.round((value + Number.EPSILON) * factor) / factor;
    }

    public getTooltipKey(shopCurrency: string | null, hasWayforpay: boolean | null): string | null {
        const priceCurrency = this.element?.prices?.[0]?.currency;
        if (priceCurrency && shopCurrency && priceCurrency !== shopCurrency) {
            return 'widget_product_catalog_card_tooltip';
        }

        if (this.isSubscriptionType && !hasWayforpay) {
            return 'widget_catalog_subscription_payment_tooltip';
        }

        return null;
    }

    public getIsDisabled(shopCurrency: string | null, hasWayforpay: boolean | null): boolean {
        const priceCurrency = this.element?.prices?.[0]?.currency;
        const isCurrencyMismatch = Boolean(priceCurrency && shopCurrency && priceCurrency !== shopCurrency);
        const isSubscriptionBlocked = this.isSubscriptionType && !hasWayforpay;

        return isCurrencyMismatch || isSubscriptionBlocked;
    }

    public get isSubscriptionType() {
        return Boolean(this.element?.recurringMode);
    }

    public get subscribePeriodLabel(): string {
        switch (this.element.recurringMode) {
            case RecurringMode.daily:
                return 'widget_product_subscription_period_day';
            case RecurringMode.weekly:
                return 'widget_product_subscription_period_week';
            case RecurringMode.monthly:
                return 'widget_product_subscription_period_month';
            case RecurringMode.quarterly:
                return 'widget_product_subscription_period_3_months';
            case RecurringMode.halfyearly:
                return 'widget_product_subscription_period_6_months';
            case RecurringMode.yearly:
                return 'widget_product_subscription_period_year';
            default:
                return 'widget_product_subscription_period_month';
        }
    }
}
